import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendar, faEdit, faTrash, faLayerGroup } from "@fortawesome/free-solid-svg-icons";
import { Modal } from "react-responsive-modal";
import { useToasts } from "react-toast-notifications";
import { useDispatch } from "react-redux";
import Button from "components/CustomButton";
import CourseService from "services/CourseService";
import * as timelineActions from "reteamStore/actions";
import { useTeamActionContext } from "store/contexts/teamActionContext";
import { useTeamContext } from "store/contexts/teamContext";
import { ReactComponent as CloseIcon } from "assets/img/icons/cancel.svg";
import { truncateText } from "utils/helpers";

const CourseCard = ({ course, teamId, isAssignedCourse }) => {
  const history = useHistory();
  const { addToast } = useToasts();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const dispatch = useDispatch();
  const { getAllCourses } = useTeamActionContext();
  const { team } = useTeamContext();
  
  const effectiveTeamId = teamId || team?.id;

  const getStatusClass = (status) => {
    switch (status) {
      case "published":
        return "success";
      case "draft":
        return "secondary";
      default:
        return "info";
    }
  };

  const getEditUrl = () => {
    return effectiveTeamId 
      ? `/teams/${effectiveTeamId}/coursebuilder/${course.id}/edit`
      : `/course-builder/${course.id}/edit`;
  };

  const handleDeleteCourse = (id) => {
    console.log("Delete course with ID:", id, "Team ID:", effectiveTeamId);
    setShowConfirmModal(true);
  };

  const confirmDeleteCourse = async () => {
    if (!effectiveTeamId) {
      console.error("Cannot delete course: No team ID available");
      setShowConfirmModal(false);
      return;
    }

    try {
      setIsDeleting(true);
      console.log(`Deleting course ${course.id} from team ${effectiveTeamId}`);
      
      // Call the delete course API
      await CourseService.deleteCourse(effectiveTeamId, course.id);
      
      // Update the Redux store
      dispatch(timelineActions.deleteCourse(course.id));
      
      // Refresh the courses list
      getAllCourses(1);
      
      setIsDeleting(false);
      setShowConfirmModal(false);
    } catch (error) {
      console.error("Error deleting course:", error);
      addToast(error.response?.data?.message || `Failed to update course. Please try again.`, { appearance: "error" });
      setIsDeleting(false);
    }
  };

  return (
    <>
      <div className="col-12 col-sm-6 col-md-4 col-lg-6 col-xl-6 mb-4">
        <div className="card h-100 d-flex flex-column course-list-card">
          <div className="course-card-cover position-relative" style={{height: '180px', background: '#f5f5f5', borderTopLeftRadius: '6px', borderTopRightRadius: '6px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
            {course.thumbnail ? (
              <img src={course.thumbnail} alt="Course Cover" style={{width: '100%', height: '100%', objectFit: 'cover'}} />
            ) : (
              <img src="https://images.pexels.com/photos/4050315/pexels-photo-4050315.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2" alt="Course Cover" style={{width: '100%', height: '100%', objectFit: 'cover'}} />
            )}
            <span className={`course-status-pill badge bg-${getStatusClass(course?.status)} position-absolute top-0 end-0 m-2`}>{course?.status?.charAt(0).toUpperCase() + course?.status?.slice(1)}</span>
          </div>
          <div className="card-body flex-grow-1 d-flex flex-column">
            <h5 className="card-title mb-1 text-truncate"><strong>{course.title}</strong></h5>
            <p className="card-text text-muted mb-2">{truncateText(course.description, 200)}</p>
            <div className="d-flex align-items-center mb-2 gap-3">
              <span className="d-flex align-items-center"><FontAwesomeIcon icon={faLayerGroup} className="me-1 text-primary" /><small className="text-muted">{course.modules.length} {course.modules.length === 1 ? 'module' : 'modules'}</small></span>
              <span className="d-flex align-items-center"><FontAwesomeIcon icon={faCalendar} className="me-1 text-secondary" /><small className="text-muted">{course.createdAt ? moment(course.createdAt).format("MMM DD, YYYY") : ''}</small></span>
            </div>
            <div className="d-flex align-items-center mt-auto gap-2">
              <Button buttonClass="btn btn-link p-0 manage-course-link" onClick={() => history.push(effectiveTeamId ? `/teams/${effectiveTeamId}/coursebuilder/${course.id}/manage` : `/course-builder/${course.id}/manage`)}>
                {isAssignedCourse ? "View Course" : "Manage Course"} &rarr;
              </Button>
              {!isAssignedCourse && (
                <>
                  <Button buttonClass="icon-btn-edit ms-auto" onClick={() => history.push(getEditUrl())} title="Edit Course">
                    <FontAwesomeIcon icon={faEdit} />
                  </Button>
                  <Button buttonClass="icon-btn-delete" onClick={() => handleDeleteCourse(course.id)} title="Delete Course">
                    <FontAwesomeIcon icon={faTrash} />
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal for Course Deletion */}
      <Modal
        open={showConfirmModal}
        onClose={() => !isDeleting && setShowConfirmModal(false)}
        showCloseIcon={false}
        closeOnEsc={!isDeleting}
        closeOnOverlayClick={!isDeleting}
        classNames={{
          modalContainer: "modalContainer",
          modal: "createCourseModal",
        }}
      >
        <div className="modal fade popup-modal create-task-popup">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="text-center createTaskHeading">Delete Course</h5>
            {!isDeleting && <CloseIcon style={{ width: "20px", cursor: "pointer" }} onClick={() => setShowConfirmModal(false)} />}
          </div>
          <div className="mb-4">
            <p>Are you sure you want to delete this course? This action cannot be undone.</p>
          </div>
          <div className="d-flex justify-content-end">
            <Button buttonClass="btn-secondary me-2" onClick={() => setShowConfirmModal(false)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button buttonClass="btn-danger" onClick={confirmDeleteCourse} disabled={isDeleting}>
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default CourseCard;